import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  HttpLink,
  Observable
} from '@apollo/client';
import { Defer20220824Handler } from '@apollo/client/incremental';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { LocalState } from '@apollo/client/local-state';
import type { PureFunction } from '@bonsai-components/utility-types';
import type { GraphQLFormattedError } from 'graphql';
import { getSession, signOut } from 'next-auth/react';
import { map } from 'rxjs';
import { isAServerError } from '../components/utilities/graphql-error/graphql-error.component';
import {
  DGS_ERROR_PREAMBLE_REGEX,
  GRAPHQL_ENDPOINT_PATH,
  GRAPHQL_INTERNAL_ERROR,
  GRAPHQL_INTERNAL_ERROR_MESSAGE,
  GRAPHQL_QUERY_COMPLEXITY_CLASSIFICATION,
  GRAPHQL_QUERY_COMPLEXITY_CODE
} from '../constants/general';
import type { NotificationContextState } from '../contexts/notification.context';
import { createApolloCache } from './apollo-client-policy.helper';
import { REFRESH_TOKEN_EXPIRED } from './keycloak.helper';
import { logError } from './logger.helper';
import { getTenantName, signInWithError } from './next.helpers';
import { createPerformanceTracker } from './performance.helpers';
import { parseJwt } from './string.helper';

const batchHttpLink = (apiGatewayUrl: string) =>
  new BatchHttpLink({
    uri: `${apiGatewayUrl}${GRAPHQL_ENDPOINT_PATH}`,
    batchMax: 20,
    fetch: appendOperation
  });

const conditionalHttpLink = (apiGatewayUrl: string) => {
  return new ApolloLink((operation, forward) => forward(operation)).split(
    (operation) => operation.getContext().batch !== undefined,
    batchHttpLink(apiGatewayUrl),
    httpLink(apiGatewayUrl)
  );
};
/**
 * Sanitizes (never drops) each error's message so the query result still
 * reports an error occurred — dropping internal errors entirely used to
 * leave `errors: []`, which erases the failure and renders identically to
 * "no data". Visible for testing.
 */
export const sanitizeGraphQLErrors = (
  errors: ReadonlyArray<GraphQLFormattedError>,
  operationName: string
): GraphQLFormattedError[] =>
  errors.map((error) => {
    const isInternal = GRAPHQL_INTERNAL_ERROR.test(error.message);
    const modifiedError = {
      ...error,
      message: isInternal
        ? GRAPHQL_INTERNAL_ERROR_MESSAGE
        : /**
           * Removes DGS exception preamble from error message
           * example:
           * com.netflix.graphql.dgs.exceptions.DgsEntityNotFoundException: Branch not found.
           *  -> Branch not found.
           */
          error.message.replace(DGS_ERROR_PREAMBLE_REGEX, '')
    };
    // Log the raw message (not `modifiedError.message`) so internal errors
    // keep their real diagnostic detail in logs even though the user-facing
    // message is now generic.
    logError({
      name: String(modifiedError.extensions?.code),
      message: `[${operationName}]: ${error.message}`
    });
    return modifiedError;
  });

/**
 * The query-complexity budget warning (moderne-saas#1745) rides along with a complete
 * response, so it is a signal for us rather than a failure for the user. It is reported
 * out-of-band and never forwarded — with the client on `errorPolicy: 'all'` it would
 * otherwise land on every consuming component's `error` and replace a working page with
 * an error state.
 *
 * Matched on two keys because the serialized shape could not be verified before this
 * shipped: the subgraph sets `extensions.code` explicitly, but it also tags the error
 * `errorType(QUERY_TOO_COMPLEX)`, which graphql-java serializes to
 * `extensions.classification`, and the router rewrites subgraph extensions on the way
 * through (an observed dev error carried `classification`/`errorType`/`service` and no
 * `code` at all). Matching either is safe: this only ever *suppresses* alongside a
 * non-null `data`, so the fatal form of the same error still reaches the UI.
 * Visible for testing.
 */
export const isComplexityWarning = (error: GraphQLFormattedError): boolean =>
  error.extensions?.code === GRAPHQL_QUERY_COMPLEXITY_CODE ||
  error.extensions?.classification === GRAPHQL_QUERY_COMPLEXITY_CLASSIFICATION;

const complexityWarningMessage = (
  error: GraphQLFormattedError,
  operationName: string
): string => {
  const { cost, max } = error.extensions ?? {};
  const budget =
    cost === undefined || max === undefined
      ? error.message
      : `estimated cost ${cost} exceeds the budget of ${max}`;
  return `[${operationName}]: GraphQL query complexity — ${budget}`;
};

/**
 * Operations already reported, so a polling query does not re-warn on every tick. Keyed
 * on the operation name and not the message: the message embeds the cost estimate, which
 * varies with variables like page size, so keying on it would both re-warn for the same
 * over-budget query and grow this set without bound. Never cleared, so an operation warns
 * at most once per page load.
 */
const reportedComplexityWarnings = new Set<string>();

/**
 * The Moderne-operated tenants the complexity budget is switched on for
 * (moderne-saas#1745). Enumerated rather than matched on a `dev` prefix so a customer
 * tenant that merely starts with those letters doesn't start showing our toast.
 */
const COMPLEXITY_BUDGET_TENANTS = new Set(['dev', 'devaz']);

/**
 * Vercel preview deploys, which build against the dev gateway — so the budget is live
 * there and the audience is whoever is reviewing the PR. Matched on the host because
 * `VERCEL`/`VERCEL_ENV` are server-only: `next.config.ts` exposes just `BUILD_ID` to the
 * browser bundle. Visible for testing.
 */
export const isPreviewHost = (host: string): boolean =>
  host.endsWith('.vercel.app');

/**
 * Where the complexity toast is worth showing — anywhere the budget is switched on and a
 * developer is looking. `NODE_ENV` alone is wrong: the tenants below are deployed
 * environments where it is `'production'`. `TENANT_NAME` is server-only, so the tenant
 * has to come from the host, and `getTenantName()` returns `'app'` on localhost, hence
 * the separate local-dev check.
 */
const shouldToastComplexityWarning = (): boolean =>
  process.env.NODE_ENV === 'development' ||
  COMPLEXITY_BUDGET_TENANTS.has(getTenantName()) ||
  (typeof window !== 'undefined' && isPreviewHost(window.location.host));

const reportComplexityWarnings = (
  warnings: ReadonlyArray<GraphQLFormattedError>,
  operationName: string,
  showError: NotificationContextState['renderNotification']
): void => {
  if (warnings.length === 0 || reportedComplexityWarnings.has(operationName)) {
    return;
  }
  reportedComplexityWarnings.add(operationName);
  for (const warning of warnings) {
    const message = complexityWarningMessage(warning, operationName);
    logError({ name: GRAPHQL_QUERY_COMPLEXITY_CODE, message });
    if (shouldToastComplexityWarning()) {
      showError('warning', message, {
        placement: { horizontal: 'center', vertical: 'top' }
      });
    }
  }
};

const errorLink = (showError: NotificationContextState['renderNotification']) =>
  new ErrorLink(({ error, result, operation, forward }) => {
    const response = result;
    const graphQLErrors = CombinedGraphQLErrors.is(error) ? error.errors : null;
    const networkError = !CombinedGraphQLErrors.is(error) ? error : null;
    if (graphQLErrors && response) {
      const remaining = graphQLErrors.filter(
        (graphQLError) => !isComplexityWarning(graphQLError)
      );
      reportComplexityWarnings(
        graphQLErrors.filter(isComplexityWarning),
        operation.operationName,
        showError
      );
      // Suppress the warning only when its premise holds — that it rode along with a
      // response that actually carries data. Without the `data` check a subgraph that
      // starts *rejecting* over-budget operations (or any mutation, which flows through
      // this link but keeps Apollo's 'none' policy) would resolve with no data and no
      // error: the exact silent failure this policy change exists to remove.
      if (remaining.length === 0 && response.data != null) {
        delete response.errors;
      } else {
        response.errors = sanitizeGraphQLErrors(
          remaining.length > 0 ? remaining : graphQLErrors,
          operation.operationName
        );
      }
    }
    if (networkError) {
      logError(networkError);
      if (isAServerError(networkError) && networkError.statusCode === 401) {
        // check expiration time of accessToken from `authorizationHeader`
        // if the expiration time has not lapsed unexpired, token was revoked by some means so redirect to sign-in page
        // otherwise request a new session and retry the operation
        const requestHeaders = operation.getContext().headers;
        const token = requestHeaders?.['Authorization']?.substring(7);

        const parsed = parseJwt(token as string);
        if (!parsed?.exp) {
          // Token is missing or malformed - redirect to sign in
          return signInWithError(
            'invalidToken',
            window.location.pathname + window.location.search,
            'invalidToken'
          );
        }
        const accessTokenExpiration = new Date(parsed.exp * 1000).getTime();
        const currentTime = new Date().getTime();

        /**
         * Special case for unexpired token that are no longer valid.
         * This can happen if the token was revoked or the user was removed from
         * the system e.g. in Keycloak restarted)
         */
        if (currentTime < accessTokenExpiration) {
          return signInWithError(
            'invalidToken',
            window.location.pathname + window.location.search,
            'unauthorizedTokenUnexpired'
          );
        }

        return new Observable((observer) => {
          getSession()
            .then((data) => {
              if (data?.error === REFRESH_TOKEN_EXPIRED) {
                throw new Error(REFRESH_TOKEN_EXPIRED);
              }
              if (data !== undefined) {
                operation.setContext({
                  headers: {
                    ...requestHeaders,
                    authorization: `Bearer ${data.accessToken}`
                  }
                });
                forward(operation).subscribe(observer);
              }
            })
            .catch(() => {
              signInWithError(
                'invalidToken',
                window.location.pathname + window.location.search,
                'refetchSessionFailed'
              );
            });
        });
      } else if (
        isAServerError(networkError) &&
        networkError.statusCode === 429
      ) {
        showError(
          'error',
          'Rate limit exceeded. Please try again in a few minutes.',
          {
            placement: {
              horizontal: 'center',
              vertical: 'top'
            }
          }
        );
      }
    }
  });

const appendOperation = (uri: string, options: RequestInit) => {
  try {
    const fetchUri = new URL(uri);

    const headerOperationName = options.headers?.['x-mod-operation-name'];
    if (headerOperationName) {
      fetchUri.searchParams.append('op', headerOperationName);
    } else if (options.body) {
      const { operationName } = JSON.parse(options.body as string);
      if (operationName) {
        fetchUri.searchParams.append('op', operationName);
      }
    }

    return fetch(fetchUri.toString(), options);
  } catch {
    return fetch(uri, options);
  }
};

const httpLink = (apiGatewayUrl: string): ApolloLink =>
  new HttpLink({
    uri: `${apiGatewayUrl}${GRAPHQL_ENDPOINT_PATH}`,
    fetch: appendOperation
  });

const getModerneApiAuthLink = (
  getAccessToken: () => string | undefined
): ApolloLink =>
  new SetContextLink(async ({ headers, ...context }) => {
    const customHeaders: Record<string, string> = {};
    // TODO: Reenable when Federation doesn't block on this header
    // if (context?.operationName) {
    //   customHeaders['x-mod-operation-name'] = context.operationName;
    // }

    // PERF: read per request, not captured at client creation, so a refreshed
    // token doesn't need a new client and a new, empty cache.
    let token = getAccessToken();

    try {
      const { exp } = parseJwt(token);
      const accessTokenExpiration = new Date(exp * 1000).getTime();
      const currentTime = new Date().getTime();

      if (currentTime > accessTokenExpiration) {
        try {
          const newToken = await getSession();
          token = newToken?.['accessToken'];
        } catch (e) {
          logError(e);
          return signOut();
        }
      }

      if (token) {
        customHeaders['Authorization'] = `Bearer ${token}`;
      }
    } catch (e) {
      logError(e);
    }

    return {
      headers: {
        ...headers,
        ...customHeaders
      },
      ...context
    };
  });

const createPerformanceTrackingLink = (): ApolloLink => {
  const performanceTracker = createPerformanceTracker('graphql');

  return new ApolloLink((operation, forward) => {
    const operationName = operation.operationName || 'unnamed_operation';

    // Start tracking the GraphQL operation
    performanceTracker.start(operationName);

    return forward(operation).pipe(
      map((response) => {
        // End tracking and get duration
        performanceTracker.end(operationName);

        return response;
      })
    );
  });
};

type ApolloClientCreator = {
  getAccessToken: () => string | undefined;
  apiGatewayUrl: string;
  showError: NotificationContextState['renderNotification'];
};

export const createApolloClient: PureFunction<
  ApolloClientCreator,
  ApolloClient
> = ({ getAccessToken, apiGatewayUrl, showError }) => {
  const apolloInMemoryCache = createApolloCache();

  // Create LocalState instance to handle @client fields
  // We use empty resolvers since our @client fields are computed via field policies
  const localState = new LocalState({});

  const client = new ApolloClient({
    link: ApolloLink.from([
      errorLink(showError),
      getModerneApiAuthLink(getAccessToken),
      createPerformanceTrackingLink(),
      conditionalHttpLink(apiGatewayUrl)
    ]),
    incrementalHandler: new Defer20220824Handler(),

    cache: apolloInMemoryCache,

    // Enable client-side fields
    assumeImmutableResults: false,

    // Configure local state for @client fields
    localState,

    devtools: {
      enabled: true,
      name: 'moderne'
    },

    // `errorPolicy: 'all'` so a failed operation surfaces as an error rather than as an
    // empty page. Consumers must expect data *and* error together on a partial response —
    // see doc/adr/0012-default-apollo-client-error-policy-to-all.md.
    defaultOptions: {
      query: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      },
      watchQuery: {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
        errorPolicy: 'all'
      }
    }
  });

  return client;
};
